import { useResources } from '@/hooks/use-resources';
import { EnumScreenName } from '@calm/evexia-analytics';
import { ActionDialog } from '@rhg/components/action-dialog';
import { ActionDataProps } from '@rhg/components/action-dialog/types';
import { FullPagePaddedContainer } from '@rhg/components/full-page-container';
import { useMostRecentSponsorship } from '@rhg/hooks/use-most-recent-sponsorship';
import { Box } from '@rhg/libs/material';
import { useState, useCallback } from 'react';
import { ConditionalWrapper } from '../conditional-wrapper';
import ResourcesCarouselCard from './card';
import ResourcesCarouselHeading from './heading';
import ResourcesCarouselList from './list';
import ResourcesCarouselSmartList from './smart-list';

interface ResourcesCarouselProps {
    variant?: 'list' | 'card' | 'smart';
    themes?: string[];
    source?: EnumScreenName;
    isDynamic?: boolean;
}

export default function ResourcesCarousel({
    variant,
    themes,
    source,
    isDynamic = false,
}: ResourcesCarouselProps) {
    const { resources } = useResources({ themes });
    const { mostRecentSponsorship } = useMostRecentSponsorship();
    const { partnerDisplayName } = mostRecentSponsorship || {};
    const [actionDialogData, setActionDialogData] = useState<
        ActionDataProps | undefined
    >();

    const handleActionSheetClose = useCallback(
        () => setActionDialogData(undefined),
        []
    );

    if (!resources?.length || (variant === 'smart' && !themes)) {
        return;
    }

    return (
        <>
            <ConditionalWrapper
                condition={!isDynamic && variant === 'card'}
                Wrapper={FullPagePaddedContainer}
            >
                <ResourcesCarouselHeading
                    variant={variant}
                    resources={resources}
                    partnerDisplayName={partnerDisplayName}
                    source={source}
                />
            </ConditionalWrapper>
            <Box sx={{ width: '100%' }}>
                {variant === 'card' && (
                    <ResourcesCarouselCard
                        resources={resources}
                        setActionDialogData={setActionDialogData}
                        source={source}
                        partnerDisplayName={partnerDisplayName}
                        isDynamic={isDynamic}
                    />
                )}
                {variant === 'list' && (
                    <ResourcesCarouselList
                        resources={resources}
                        setActionDialogData={setActionDialogData}
                        partnerDisplayName={partnerDisplayName}
                    />
                )}
                {variant === 'smart' && (
                    <ResourcesCarouselSmartList
                        themes={themes}
                        setActionDialogData={setActionDialogData}
                        source={source}
                        partnerDisplayName={partnerDisplayName}
                    />
                )}
            </Box>
            {actionDialogData && (
                <ActionDialog
                    variant="resource"
                    onClose={handleActionSheetClose}
                    actionData={actionDialogData}
                />
            )}
        </>
    );
}
